/* ==========================================================================
   PDP animations — scroll reveal, staggered entrances and stat count-ups.

   Progressive enhancement & accessibility:
   - The CSS only hides reveal targets under `html.pdp-js`, and this script is
     the only thing that adds that class. So if the script never loads/runs,
     nothing is ever left hidden.
   - Bails out entirely when the visitor prefers reduced motion.
   - Re-scans sections re-rendered by the Shopify theme editor.
   ========================================================================== */
/* ==========================================================================
   Image loading — runs for everyone, including reduced-motion visitors.

   - Images still downloading inside a media panel get .img-wait, which the
     CSS uses to show a charcoal shimmer instead of a flat black box; when the
     image arrives it swaps to .img-in and fades up. Without this script
     nothing is ever hidden.
   - Once the page has loaded, the remaining lazy images are asked for in the
     background, so they are usually ready before the visitor scrolls to them
     instead of starting to download only as they come into view.
   ========================================================================== */
(() => {
  if (window.__imgLoaderInit) return;
  window.__imgLoaderInit = true;

  const MEDIA = [
    '.brf-split__media img',
    '.brf-doctrine__media img',
    '.brf-cta__media img',
    '.brf-video img',
    '.brf-pcard__media img',
    '.bnd-stage__card img',
    '.bnd-card__media img',
    '.bnd-option__thumb img',
    '.bnd-option__media img',
    '.pdp-split__media img',
    '.pdp-story__media img',
    '.pdp-figure img',
  ].join(', ');

  document.documentElement.classList.add('img-js');

  function settle(img) {
    img.classList.remove('img-wait');
    img.classList.add('img-in');
  }

  function track(img) {
    if (img.dataset.imgTracked) return;
    img.dataset.imgTracked = '1';
    if (img.complete && img.naturalWidth > 0) return; // already painted, no fade
    // above-the-fold images (the hero) may be half drawn by now; hiding them
    // would flicker, so they render as they arrive
    if (img.loading === 'eager' || img.fetchPriority === 'high') return;
    img.classList.add('img-wait');
    img.addEventListener('load', () => settle(img), { once: true });
    // a broken image must never stay hidden
    img.addEventListener('error', () => settle(img), { once: true });
  }

  function warm(scope) {
    scope.querySelectorAll(MEDIA).forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
    });
  }

  function scan(scope) {
    scope.querySelectorAll(MEDIA).forEach(track);
  }

  const start = () => {
    scan(document);
    // ask for the rest once the first view is done, without competing with it
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    const later = () => idle(() => warm(document), { timeout: 2500 });
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // cards fetched after load (recommendations) and editor re-renders
  new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.(MEDIA)) track(node);
        else if (node.querySelectorAll) scan(node);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

(() => {
  if (window.__pdpAnimInit) return;
  window.__pdpAnimInit = true;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.documentElement.classList.add('pdp-js');

  const SINGLES = [
    '.pdp-head, .pdp-split__media, .pdp-story__media',
    // briefing sections — the hero has its own entrance, so it is left out
    '.brf:not(.brf-hero) :is(.brf-chip, .brf-title, .brf-lead, .brf-note, .brf-split__media)',
    '.brf :is(.brf-doctrine__media, .brf-video, .brf-disclaimer, .brf-cta__media, .brf-cta__name, .brf-cta__price, .brf-cta__meta, .brf-cta__kicker)',
    // bundle pages
    '.bnd-lineup__head, .bnd-urgency, .bnd-compare__head, .bnd-routine__head',
    // conversion product page
    '.sell-head, .sell-guarantee, .sell-split__media, .sell-split__body, .sell-compare',
    // site chrome
    '.site-footer__marquee, .site-footer__statement--static',
  ].join(', ');
  const GROUPS = [
    '.pdp-grid, .pdp-steps, .pdp-testi__grid, .pdp-featurelist, .pdp-logos, .pdp-story__stats, .pdp-faqs',
    '.brf-pair, .brf-cards, .brf-actives, .brf-matrix, .brf-steps-chev, .brf-badges, .brf-deploy',
    '.brf-results, .brf-cta__grid, .brf-pills, .brf-grid-list, .brf-arrows',
    '.bnd-eq, .bnd-compare__grid, .brf-shop__grid, .bnd-routine__cols',
    '.sell-pillars, .sell-ingredients, .sell-timeline, .sell-points, .sell-reviews__track',
  ].join(', ');
  const COUNTERS = '.pdp-fcard__stat, .pdp-story__stat-num';

  const supported = 'IntersectionObserver' in window;

  const io = supported
    ? new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target;
            obs.unobserve(el);
            if (el.dataset.pdpCount !== undefined) countUp(el);
            else el.classList.add('pdp-in');
          }
        },
        { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
      )
    : null;

  function reveal(el, cls) {
    if (el.classList.contains(cls)) return;
    el.classList.add(cls);
    if (io) io.observe(el);
    else el.classList.add('pdp-in');
  }

  function markCounter(el) {
    if (el.dataset.pdpInit) return;
    el.dataset.pdpInit = '1';
    const match = /^(\D*?)(\d[\d,]*)(.*)$/s.exec(el.textContent.trim());
    if (!match) return;

    const grouped = /,/.test(match[2]);
    el.dataset.pdpCount = '';
    el.dataset.pdpPrefix = match[1];
    el.dataset.pdpTarget = match[2].replace(/,/g, '');
    el.dataset.pdpSuffix = match[3];
    el.dataset.pdpGroup = grouped ? '1' : '0';

    if (io) {
      el.textContent = match[1] + (grouped ? (0).toLocaleString() : '0') + match[3];
      io.observe(el);
    }
  }

  function countUp(el) {
    const target = parseInt(el.dataset.pdpTarget, 10);
    const prefix = el.dataset.pdpPrefix || '';
    const suffix = el.dataset.pdpSuffix || '';
    const grouped = el.dataset.pdpGroup === '1';
    const fmt = (n) => (grouped ? n.toLocaleString() : String(n));
    const duration = 1300;
    let started;

    const step = (now) => {
      if (started === undefined) started = now;
      const t = Math.min(1, (now - started) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = prefix + fmt(Math.round(eased * target)) + suffix;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ---- FAQ accordion: smooth open AND close (height + fade) -------------- */
  const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  // fill:'forwards' holds the end frame until we commit the resting inline style
  // and cancel(), so the panel never reverts to the pre-finish inline height for
  // a frame (the classic WAAPI end-of-animation flash). overflow is toggled only
  // during the animation so a focused link in an open answer isn't clipped.
  function openFaq(faq, panel) {
    faq.classList.add('pdp-faq--open');
    faq.open = true;
    panel.style.overflow = 'hidden';
    panel.style.height = '0px';
    const full = panel.scrollHeight;
    const anim = panel.animate(
      [
        { height: '0px', opacity: 0 },
        { height: full + 'px', opacity: 1 },
      ],
      { duration: 320, easing: EASE, fill: 'forwards' }
    );
    return anim.finished
      .then(() => {
        panel.style.height = '';
        panel.style.overflow = '';
        anim.cancel();
      })
      .catch(() => {});
  }

  function closeFaq(faq, panel) {
    faq.classList.remove('pdp-faq--open');
    panel.style.overflow = 'hidden';
    const full = panel.scrollHeight;
    const anim = panel.animate(
      [
        { height: full + 'px', opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 280, easing: EASE, fill: 'forwards' }
    );
    return anim.finished
      .then(() => {
        faq.open = false;
        panel.style.height = '';
        panel.style.overflow = '';
        anim.cancel();
      })
      .catch(() => {});
  }

  function setupFaq(faq) {
    if (faq.dataset.pdpFaq) return;
    faq.dataset.pdpFaq = '1';
    const summary = faq.querySelector('.pdp-faq__summary');
    const panel = faq.querySelector('.pdp-faq__reveal');
    if (!summary || !panel || typeof faq.animate !== 'function') return;

    // Marks this FAQ as JS-controlled so the icon CSS keys off .pdp-faq--open;
    // FAQs where we bail (no WAAPI) keep the native [open] icon behaviour.
    faq.classList.add('pdp-faq--js');
    if (faq.open) faq.classList.add('pdp-faq--open');

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (faq.dataset.pdpBusy) return;
      faq.dataset.pdpBusy = '1';
      const done = faq.open ? closeFaq(faq, panel) : openFaq(faq, panel);
      done.then(() => {
        delete faq.dataset.pdpBusy;
      });
    });

    // Keep the icon in sync if the panel is opened/closed without a summary
    // click (e.g. Chrome find-in-page expands a <details> by setting the open
    // attribute, which fires 'toggle', not 'click').
    faq.addEventListener('toggle', () => {
      if (faq.dataset.pdpBusy) return;
      faq.classList.toggle('pdp-faq--open', faq.open);
    });
  }

  function scan(scope) {
    scope.querySelectorAll(SINGLES).forEach((el) => reveal(el, 'pdp-reveal'));
    scope.querySelectorAll(GROUPS).forEach((el) => reveal(el, 'pdp-stagger'));
    scope.querySelectorAll(COUNTERS).forEach((el) => markCounter(el));
    scope.querySelectorAll('.pdp-faq').forEach(setupFaq);
  }

  const run = () => scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  // Theme editor: re-scan a section after it is re-rendered.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
